/**
 * LiveClassRoomPage — embeds the Zoom Meeting SDK in **Client View** so
 * students and hosts get the exact same UI they would in a fresh browser tab
 * joining a real Zoom meeting: full-viewport video, gallery/speaker toggle,
 * dockable mic/camera controls, participants panel, chat drawer, screen share,
 * host controls, etc.
 *
 * Why Client View, not Component View?
 * -----------------------------------
 * We started with the Embedded/Component View (`zoom-meeting-embedded-*.js`)
 * which renders the meeting as a draggable, resizable floating window inside
 * a container. That view's internal `zoom-MuiPaper` container ignores the
 * `viewSizes` we pass and always renders at ~2190px tall — the participant
 * tile ended up below our viewport and the meeting appeared "blank".
 * Client View (`zoom-meeting-*.js`) mounts a `#zmmtg-root` element on
 * document.body and takes over the viewport, giving the native meeting UX
 * that users expect.
 *
 * On leave (either via Zoom's own leave button or ours), Zoom navigates the
 * browser to `leaveUrl`. We point it at our live-classes list so the user
 * lands somewhere useful.
 *
 * Routes:
 *   /live-classes/:classId/join         → attendee (role=0)
 *   /admin/live-classes/:classId/host   → host (role=1)
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Loader2, AlertCircle, Monitor, Chrome, Info, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pin to a specific SDK version — matches package.json for reference.
const ZOOM_SDK_VERSION = '3.13.2';
const ZOOM_CDN_BASE = `https://source.zoom.us/${ZOOM_SDK_VERSION}`;

// ---------- Browser detection for host launcher UX ----------
// Zoom Meeting SDK 3.x officially supports Chrome, Edge and Firefox on
// desktop. Brave blocks SharedArrayBuffer via Shields, Safari has SDK-3
// WebAssembly crashes on screen share. We surface a soft warning so hosts
// pick the right browser (or fall back to the Desktop App) before joining.
const detectBrowser = () => {
  if (typeof navigator === 'undefined') return { name: 'unknown', supported: true };
  const ua = navigator.userAgent || '';
  // Brave exposes navigator.brave.isBrave() — fall back to UA hint if the API is unavailable.
  const isBrave = (navigator.brave && typeof navigator.brave.isBrave === 'function') || /Brave/i.test(ua);
  if (isBrave) return { name: 'Brave', supported: false, reason: 'Brave Shields blocks the browser feature Zoom needs for screen sharing.' };
  const isEdge = /Edg\//.test(ua);
  if (isEdge) return { name: 'Edge', supported: true };
  const isChrome = /Chrome\//.test(ua) && !/Chromium/.test(ua) && !/Edg\//.test(ua);
  if (isChrome) return { name: 'Chrome', supported: true };
  const isFirefox = /Firefox\//.test(ua);
  if (isFirefox) return { name: 'Firefox', supported: true };
  const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Chromium/.test(ua);
  if (isSafari) return { name: 'Safari', supported: false, reason: 'Safari has known screen-share crashes with Zoom Web SDK 3.x.' };
  return { name: 'this browser', supported: true };
};

// ---------- CDN script/CSS loaders ----------
const loadScript = (src, globalName) => new Promise((resolve, reject) => {
  if (globalName && window[globalName]) return resolve(window[globalName]);
  const existing = document.querySelector(`script[data-src="${src}"]`);
  if (existing) {
    existing.addEventListener('load', () => resolve(globalName ? window[globalName] : true));
    existing.addEventListener('error', reject);
    return;
  }
  const s = document.createElement('script');
  s.src = src;
  s.async = true;
  s.dataset.src = src;
  s.onload = () => resolve(globalName ? window[globalName] : true);
  s.onerror = () => reject(new Error(`Failed to load ${src}`));
  document.head.appendChild(s);
});

const loadStylesheet = (href) => new Promise((resolve, reject) => {
  if (document.querySelector(`link[data-href="${href}"]`)) return resolve(true);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.dataset.href = href;
  link.onload = () => resolve(true);
  link.onerror = () => reject(new Error(`Failed to load ${href}`));
  document.head.appendChild(link);
});

async function loadZoomClientSDK() {
  if (window.ZoomMtg) return window.ZoomMtg;
  // Client-View stylesheets — Zoom recommends bootstrap + react-select.
  await Promise.all([
    loadStylesheet(`${ZOOM_CDN_BASE}/css/bootstrap.css`),
    loadStylesheet(`${ZOOM_CDN_BASE}/css/react-select.css`),
  ]);
  // Vendor JS deps must be loaded in this exact order.
  await loadScript(`${ZOOM_CDN_BASE}/lib/vendor/react.min.js`, 'React');
  await loadScript(`${ZOOM_CDN_BASE}/lib/vendor/react-dom.min.js`, 'ReactDOM');
  await loadScript(`${ZOOM_CDN_BASE}/lib/vendor/redux.min.js`, 'Redux');
  await loadScript(`${ZOOM_CDN_BASE}/lib/vendor/redux-thunk.min.js`, 'ReduxThunk');
  await loadScript(`${ZOOM_CDN_BASE}/lib/vendor/lodash.min.js`, '_');
  await loadScript(`${ZOOM_CDN_BASE}/zoom-meeting-${ZOOM_SDK_VERSION}.min.js`, 'ZoomMtg');

  // Point the SDK at Zoom's CDN for its WebAssembly assets and prep the runtime.
  // Call order MATTERS — setZoomJSLib → preLoadWasm → prepareWebSDK.
  window.ZoomMtg.setZoomJSLib(`${ZOOM_CDN_BASE}/lib`, '/av');
  window.ZoomMtg.preLoadWasm();
  window.ZoomMtg.prepareWebSDK();
  try {
    window.ZoomMtg.i18n.load('en-US');
    window.ZoomMtg.i18n.reload('en-US');
  } catch (_) { /* i18n is best-effort */ }
  return window.ZoomMtg;
}

// Client View injects #zmmtg-root on document.body with `display: none` by
// default. Toggling `display: block` reveals the meeting UI. We also add a
// body class so we can hide our app chrome while the meeting is on stage.
const ROOT_ID = 'zmmtg-root';
const BODY_ACTIVE_CLASS = 'zoom-client-active';

const showZoomRoot = () => {
  document.body.classList.add(BODY_ACTIVE_CLASS);
  const root = document.getElementById(ROOT_ID);
  if (root) root.style.display = 'block';
};

const hideZoomRoot = () => {
  document.body.classList.remove(BODY_ACTIVE_CLASS);
  const root = document.getElementById(ROOT_ID);
  if (root) root.style.display = 'none';
};


const LiveClassRoomPage = ({ mode }) => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const joinedRef = useRef(false);
  const cancelledRef = useRef(false);
  const heartbeatRef = useRef(null);

  // host-ready = token fetched, waiting for host to pick Browser vs Desktop App
  const [status, setStatus] = useState('loading');   // loading | host-ready | joining | joined | error
  const [errorMsg, setErrorMsg] = useState('');
  const [tokenData, setTokenData] = useState(null);
  const [browserInfo] = useState(() => detectBrowser());

  const isHost = mode === 'host';
  const leaveUrlPath = isHost ? '/admin/live-classes' : '/live-classes';

  // ── Zoom SDK init + join (used for both host and attendee) ──────
  const launchBrowserSdk = async (data) => {
    try {
      setStatus('joining');
      const ZoomMtg = await loadZoomClientSDK();
      if (cancelledRef.current) return;
      if (joinedRef.current) return;
      joinedRef.current = true;

      const leaveUrl = `${window.location.origin}${leaveUrlPath}`;

      await new Promise((resolve, reject) => {
        ZoomMtg.init({
          leaveUrl,
          patchJsMedia: true,
          disableInvite: true,
          disableCallOut: true,
          disableRecord: !isHost,
          // ── Meeting-info leak hardening ──────────────────────────────
          // Hide meeting number / passcode from the SDK "i" panel so a
          // paid MARQ user can't casually copy them to a friend. Hosts
          // who need those details already have them in the admin
          // dashboard.
          showMeetingHeader: false,
          meetingInfo: ['topic', 'host', 'participant', 'dc', 'enctype'],
          isSupportAV: true,
          isSupportChat: true,
          isSupportQA: false,
          isSupportCC: true,
          isSupportPolling: false,
          isSupportBreakout: isHost,
          screenShare: true,
          videoDrag: true,
          sharingMode: 'both',
          videoHeader: true,
          success: resolve,
          error: reject,
        });
      });
      if (cancelledRef.current) return;

      showZoomRoot();

      await new Promise((resolve, reject) => {
        ZoomMtg.join({
          signature: data.signature,
          sdkKey: data.sdk_key,
          meetingNumber: String(data.meeting_number),
          passWord: data.meeting_password || '',
          userName: data.user_name,
          // For HOST joins we deliberately omit userEmail so the SDK
          // derives identity from the ZAK. Sending a different email
          // (the logged-in admin's) makes Zoom silently demote us to a
          // role=1 attendee and strip host powers like screen share.
          userEmail: isHost ? '' : data.user_email,
          tk: data.tk || '',
          zak: isHost ? (data.zak || '') : '',
          success: resolve,
          error: reject,
        });
      });
      if (cancelledRef.current) return;
      setStatus('joined');
    } catch (err) {
      console.error('Zoom launch failed:', err);
      if (cancelledRef.current) return;
      hideZoomRoot();
      const pickMsg = (e) => {
        if (!e) return 'Unable to join the class';
        if (typeof e === 'string') return e;
        const detail = e?.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join('; ');
        if (detail && typeof detail === 'object') return detail.message || JSON.stringify(detail);
        if (e.reason) return `Zoom: ${e.reason}${e.errorCode ? ` (code ${e.errorCode})` : ''}`;
        if (e.type && e.errorCode) return `Zoom error ${e.errorCode} (${e.type})`;
        if (e.message) return e.message;
        try { return JSON.stringify(e); } catch { return 'Unable to join the class'; }
      };
      setErrorMsg(pickMsg(err));
      setStatus('error');
    }
  };

  // ── Host chooses Desktop App: open `start_url` (contains ZAK) in a new
  // tab; the browser hands off to the `zoommtg://` protocol handler which
  // launches Zoom Desktop with the host already logged in. All server-side
  // enforcements (registration approval, disabled Zoom emails) remain
  // intact because they're set on the meeting itself, not the client.
  const launchDesktopApp = (data) => {
    if (!data?.start_url) {
      setErrorMsg('Desktop App launch URL missing. Please use "Start in Browser" instead.');
      setStatus('error');
      return;
    }
    window.open(data.start_url, '_blank', 'noopener,noreferrer');
    // Bounce back to the admin live-classes list — the meeting itself
    // continues on Zoom Desktop.
    navigate(leaveUrlPath, { replace: true });
  };

  useEffect(() => {
    cancelledRef.current = false;

    async function bootstrap() {
      try {
        setStatus('loading');
        const endpoint = isHost
          ? `${API}/admin/live-classes/${classId}/host-token`
          : `${API}/live-classes/${classId}/join-token`;
        const { data } = await axios.post(endpoint);
        if (cancelledRef.current) return;
        setTokenData(data);

        if (isHost) {
          // Pause on the launcher card so the host can pick Browser vs Desktop.
          setStatus('host-ready');
        } else {
          // Attendee: auto-launch (unchanged).
          launchBrowserSdk(data);
        }
      } catch (err) {
        console.error('Zoom bootstrap failed:', err);
        if (cancelledRef.current) return;
        const pickMsg = (e) => {
          if (!e) return 'Unable to prepare the class';
          const detail = e?.response?.data?.detail;
          if (typeof detail === 'string') return detail;
          if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join('; ');
          if (detail && typeof detail === 'object') return detail.message || JSON.stringify(detail);
          if (e.message) return e.message;
          try { return JSON.stringify(e); } catch { return 'Unable to prepare the class'; }
        };
        setErrorMsg(pickMsg(err));
        setStatus('error');
      }
    }

    bootstrap();

    // Session heartbeat — same as before, only when a Zoom session is active.
    heartbeatRef.current = setInterval(async () => {
      if (cancelledRef.current) return;
      try {
        await axios.get(`${API}/auth/session-ping`);
      } catch (err) {
        const s = err.response?.status;
        if (s === 401 || s === 403) {
          try { window.ZoomMtg?.leaveMeeting?.({}); } catch (_) { /* noop */ }
          hideZoomRoot();
          try { localStorage.removeItem('token'); } catch (_) { /* noop */ }
          navigate('/login?reason=session_ended', { replace: true });
        }
      }
    }, 30_000);

    // ── Silence Zoom SDK teardown noise ────────────────────────────────
    // Zoom Meeting SDK 3.x has a known race in stream cleanup: when a
    // video/audio/share track ends (host stops screen share, attendee
    // stops camera, or the SDK internally recycles a worker) an unhandled
    // promise rejection sometimes bubbles out of the minified bundle. It's
    // cosmetic — the meeting keeps working — but it surfaces to users as a
    // scary red overlay from React's error hook. We filter events whose
    // stack/source points at the Zoom bundle and swallow them. Everything
    // else propagates normally so real app errors still get logged.
    const isZoomOrigin = (target) => {
      if (!target) return false;
      const s = String(target);
      // The Zoom CDN bundle names all end in /zoom-meeting-*.min.js and its
      // web-workers register with names starting with "audio_worker" or
      // "video-worker". Both patterns show up in stack traces.
      return /zoom-meeting|source\.zoom\.us|audio_worker|video-worker|meeting_sharing|WebSDK/i.test(s);
    };
    const onUnhandledRejection = (event) => {
      const reasonStr = (() => {
        try { return event.reason?.stack || event.reason?.message || String(event.reason || ''); }
        catch (_) { return ''; }
      })();
      if (isZoomOrigin(reasonStr)) {
        console.debug('[Zoom SDK] Swallowed teardown promise rejection:', reasonStr.slice(0, 200));
        event.preventDefault();
      }
    };
    const onWindowError = (event) => {
      const src = event.filename || event.error?.stack || event.message || '';
      if (isZoomOrigin(src)) {
        console.debug('[Zoom SDK] Swallowed runtime error:', String(src).slice(0, 200));
        event.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('error', onWindowError, true);

    return () => {
      cancelledRef.current = true;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener('error', onWindowError, true);
      try { window.ZoomMtg?.leaveMeeting?.({}); } catch (_) { /* noop */ }
      hideZoomRoot();
      joinedRef.current = false;
    };
  }, [classId, isHost, leaveUrlPath, navigate]);

  const goBack = () => navigate(leaveUrlPath);

  // While Zoom is loading/joining we show our own centered spinner. Once
  // Client-View takes over, the #zmmtg-root on document.body covers the
  // viewport and our React tree is effectively invisible under it — no
  // additional UI needed.
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-black text-white"
      data-testid="live-class-room-page"
    >
      {(status === 'loading' || status === 'joining') && (
        <div className="flex flex-col items-center gap-3" data-testid="live-class-room-loading">
          <Loader2 className="w-10 h-10 animate-spin" style={{ color: '#7DD3FC' }} />
          <p className="text-sm text-white/80">
            {status === 'loading' ? 'Preparing your session…' : 'Connecting to Zoom…'}
          </p>
        </div>
      )}

      {status === 'host-ready' && tokenData && (
        <div
          className="w-full max-w-lg rounded-2xl p-8"
          style={{
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(125, 211, 252, 0.25)',
          }}
          data-testid="host-launcher-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <Monitor className="w-5 h-5" style={{ color: '#7DD3FC' }} />
            <p className="text-xs uppercase tracking-wider text-white/60">Host controls</p>
          </div>
          <h2 className="text-2xl font-semibold mb-1" data-testid="host-launcher-title">
            {tokenData.class_title || 'Live class'}
          </h2>
          <p className="text-sm text-white/60 mb-6">Choose how you want to run this session.</p>

          {/* Browser-support advisory. Shown to every host so they know why
              the Desktop App option exists. If we detected an unsupported
              browser (Brave / Safari) we upgrade the tone to a warning. */}
          <div
            className={`rounded-lg p-3 mb-6 flex gap-2 text-sm ${
              browserInfo.supported
                ? 'bg-blue-500/10 border border-blue-400/30 text-blue-100'
                : 'bg-amber-500/10 border border-amber-400/40 text-amber-100'
            }`}
            data-testid="host-browser-advisory"
          >
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              {browserInfo.supported ? (
                <>
                  You&apos;re on <strong>{browserInfo.name}</strong>. In-browser mode works
                  well on Chrome, Edge and Firefox.
                </>
              ) : (
                <>
                  <strong>{browserInfo.name} isn&apos;t fully supported.</strong> {browserInfo.reason}{' '}
                  Please use Chrome, Edge or Firefox in browser mode — or start in the Zoom Desktop App below.
                </>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => launchDesktopApp(tokenData)}
              className="w-full h-12 justify-center gap-2 bg-[#7DD3FC] text-slate-900 hover:bg-[#7DD3FC]/90 font-medium"
              data-testid="host-launch-desktop-btn"
              disabled={!tokenData.start_url}
            >
              <ExternalLink className="w-4 h-4" />
              Start in Zoom Desktop App
              <span className="text-[10px] uppercase tracking-wider font-semibold ml-1 px-1.5 py-0.5 rounded bg-slate-900/15">Recommended</span>
            </Button>

            <Button
              onClick={() => launchBrowserSdk(tokenData)}
              variant="outline"
              className="w-full h-12 justify-center gap-2 border-white/20 bg-transparent text-white hover:bg-white/10"
              data-testid="host-launch-browser-btn"
            >
              <Chrome className="w-4 h-4" />
              Start in Browser
            </Button>

            <div className="pt-2 space-y-1.5 text-xs text-white/50 leading-relaxed">
              <p>
                <strong className="text-white/70">Desktop App</strong> unlocks the
                cloud recording button, waiting-room + chat alerts, annotations
                and whiteboard. Recordings save to the platform&apos;s Zoom
                cloud regardless of which personal Zoom account you&apos;re
                signed into.
              </p>
              <p>
                <strong className="text-white/70">Browser</strong> is the
                fallback if Zoom Desktop isn&apos;t installed. Cloud recording
                may be hidden here; screen share works but with fewer host
                controls.
              </p>
              <p className="text-white/40 italic">
                Registration approval, disabled Zoom emails and attendee tracking apply either way.
              </p>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-white/10 flex justify-end">
            <Button
              variant="ghost"
              onClick={goBack}
              className="text-white/60 hover:text-white hover:bg-white/5"
              data-testid="host-launcher-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div
          className="max-w-md text-center rounded-xl p-8"
          style={{
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
          }}
          data-testid="live-class-room-error"
        >
          <AlertCircle className="w-10 h-10 mx-auto text-red-400 mb-3" />
          <h3 className="font-semibold mb-2">Unable to join</h3>
          <p className="text-sm text-white/70 mb-4" data-testid="live-class-room-error-msg">{errorMsg}</p>
          <Button onClick={goBack} variant="secondary">Back</Button>
        </div>
      )}
    </div>
  );
};

export default LiveClassRoomPage;
